import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import StoreService from '../services/storeService';
import requestDeduplicator from '../utils/requestDeduplicator';

const ONBOARDING_STATUS = {
  NotStarted: 'NotStarted',
  Pending: 'Pending',
  Complete: 'Complete',
};

const CARD_PAYMENTS_STATUS = {
  Active: 'active',
  Pending: 'pending',
  Inactive: 'inactive',
};

const normalizeOnboardingStatus = (status) => {
  if (!status) {
    return ONBOARDING_STATUS.NotStarted;
  }

  const normalized = `${status}`;
  if (normalized === ONBOARDING_STATUS.Complete) {
    return ONBOARDING_STATUS.Complete;
  }

  if (normalized === ONBOARDING_STATUS.Pending) {
    return ONBOARDING_STATUS.Pending;
  }

  return ONBOARDING_STATUS.NotStarted;
};

const normalizeCardPaymentsStatus = (status) => {
  if (!status) {
    return null;
  }

  const normalized = `${status}`.toLowerCase();
  if (normalized === CARD_PAYMENTS_STATUS.Active) {
    return CARD_PAYMENTS_STATUS.Active;
  }

  if (normalized === CARD_PAYMENTS_STATUS.Pending) {
    return CARD_PAYMENTS_STATUS.Pending;
  }

  if (normalized === CARD_PAYMENTS_STATUS.Inactive) {
    return CARD_PAYMENTS_STATUS.Inactive;
  }

  return normalized;
};

const normalizeOnboardingStatusResponse = (data) => ({
  connectedAccountId: data?.connectedAccountId ?? data?.ConnectedAccountId ?? null,
  onboardingStatus: normalizeOnboardingStatus(data?.onboardingStatus ?? data?.OnboardingStatus),
  detailsSubmitted: Boolean(data?.detailsSubmitted ?? data?.DetailsSubmitted),
  chargesEnabled: Boolean(data?.chargesEnabled ?? data?.ChargesEnabled),
  payoutsEnabled: Boolean(data?.payoutsEnabled ?? data?.PayoutsEnabled),
  cardPaymentsStatus: normalizeCardPaymentsStatus(data?.cardPaymentsStatus ?? data?.CardPaymentsStatus),
  membershipBillingReady: Boolean(data?.membershipBillingReady ?? data?.MembershipBillingReady),
  onboardingCompletedAt: data?.onboardingCompletedAt ?? data?.OnboardingCompletedAt ?? null,
});

const useStripeConnectOnboarding = (storeId, { enabled = true } = {}) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  const loadOnboardingStatus = useCallback(async () => {
    if (!storeId || !enabled) {
      return null;
    }

    try {
      setLoading(true);
      const data = normalizeOnboardingStatusResponse(
        await requestDeduplicator.deduplicate(
          `store-onboarding-status-${storeId}`,
          () => StoreService.getOnboardingStatus(storeId),
        ),
      );
      setOnboardingStatus(data);
      return data;
    } catch (err) {
      console.error('Error loading Stripe onboarding status:', err);

      if (err?.response?.status === 403) {
        return null;
      }

      showError(err?.response?.data?.message || t('stores.stripeConnect.errors.loadStatus'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled, showError, storeId, t]);

  useEffect(() => {
    loadOnboardingStatus();
  }, [loadOnboardingStatus]);

  const startOnboarding = useCallback(async () => {
    if (!storeId || actionLoading) {
      return;
    }

    try {
      setActionLoading(true);
      const result = await StoreService.createOnboardingLink(storeId);
      const onboardingUrl = result?.url ?? result?.Url;

      if (!onboardingUrl) {
        throw new Error(t('stores.stripeConnect.errors.invalidUrl'));
      }

      window.location.assign(onboardingUrl);
    } catch (err) {
      console.error('Error creating Stripe onboarding link:', err);

      if (err?.response?.status === 403) {
        showError(t('stores.stripeConnect.errors.ownerOnly'));
        return;
      }

      showError(err?.response?.data?.message || err?.message || t('stores.stripeConnect.errors.open'));
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, showError, storeId, t]);

  const refreshOnboardingStatus = useCallback(async () => {
    if (!storeId || actionLoading) {
      return null;
    }

    try {
      setActionLoading(true);
      await StoreService.refreshAccountStatus(storeId);
      const data = await loadOnboardingStatus();

      if (data?.membershipBillingReady) {
        showSuccess(t('stores.stripeConnect.feedback.membershipBillingReady'));
      } else if (data?.onboardingStatus === ONBOARDING_STATUS.Complete) {
        showSuccess(t('stores.stripeConnect.feedback.complete'));
      }

      return data;
    } catch (err) {
      console.error('Error refreshing Stripe account status:', err);

      if (err?.response?.status === 403) {
        showError(t('stores.stripeConnect.errors.ownerOnly'));
        return null;
      }

      showError(err?.response?.data?.message || t('stores.stripeConnect.errors.refresh'));
      return null;
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, loadOnboardingStatus, showError, showSuccess, storeId, t]);

  const isComplete = onboardingStatus?.onboardingStatus === ONBOARDING_STATUS.Complete;
  const isMembershipBillingReady = Boolean(onboardingStatus?.membershipBillingReady);
  const needsAction = Boolean(onboardingStatus) && (!isComplete || !isMembershipBillingReady);

  return {
    onboardingStatus,
    loading,
    actionLoading,
    isComplete,
    isMembershipBillingReady,
    needsAction,
    loadOnboardingStatus,
    startOnboarding,
    refreshOnboardingStatus,
  };
};

export default useStripeConnectOnboarding;
